import { useRef, useState, type CSSProperties, type ReactNode, type TouchEvent } from 'react';

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<unknown> | unknown;
  disabled?: boolean;
  style?: CSSProperties;
  contentStyle?: CSSProperties;
}

const REFRESH_THRESHOLD = 64;
const MAX_PULL_DISTANCE = 88;
const MAX_CONTENT_OFFSET = 44;

export default function PullToRefresh({
  children,
  onRefresh,
  disabled = false,
  style,
  contentStyle,
}: PullToRefreshProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const startYRef = useRef<number | null>(null);
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    if (disabled || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
      setDistance(0);
      startYRef.current = null;
    }
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    if (disabled || refreshing || scrollTop > 0) return;
    startYRef.current = event.touches[0]?.clientY ?? null;
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const startY = startYRef.current;
    const currentY = event.touches[0]?.clientY;
    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    if (startY === null || currentY === undefined || scrollTop > 0) return;

    const nextDistance = currentY - startY;
    if (nextDistance <= 0) {
      setDistance(0);
      return;
    }

    setDistance(Math.min(nextDistance * 0.55, MAX_PULL_DISTANCE));
  };

  const handleTouchEnd = () => {
    if (distance >= REFRESH_THRESHOLD) {
      void refresh();
      return;
    }
    startYRef.current = null;
    setDistance(0);
  };

  const active = distance > 10 || refreshing;
  const offset = refreshing ? MAX_CONTENT_OFFSET : Math.min(distance, MAX_CONTENT_OFFSET);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '8px',
          left: 0,
          right: 0,
          zIndex: 12,
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          opacity: active ? 1 : 0,
          transform: `translateY(${active ? 0 : -18}px)`,
          transition: refreshing || distance === 0
            ? 'opacity 0.2s ease, transform 0.2s ease'
            : 'none',
        }}
      >
        <div
          style={{
            height: '32px',
            padding: '0 14px',
            borderRadius: '16px',
            background: 'rgba(20,21,38,0.86)',
            border: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#A8B2C8',
            fontSize: '12px',
            backdropFilter: 'blur(18px)',
          }}
        >
          <i
            className={refreshing ? 'ri-loader-4-line' : 'ri-arrow-down-line'}
            style={{ fontSize: '14px', color: '#3ECFA0' }}
          />
          {refreshing
            ? 'Обновляем...'
            : distance >= REFRESH_THRESHOLD
              ? 'Отпустите, чтобы обновить'
              : 'Потяните для обновления'}
        </div>
      </div>
      <div
        ref={scrollRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehaviorY: 'contain',
          WebkitOverflowScrolling: 'touch',
          transform: `translateY(${offset}px)`,
          transition: distance === 0 || refreshing ? 'transform 0.22s ease' : 'none',
          ...contentStyle,
        }}
      >
        {children}
      </div>
    </div>
  );
}
